import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWarning } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { useForm } from '@domain-group/react-hooks'

import useCampaignItem from '@hooks/useCampaignItem'
import useOrder from '@hooks/useOrder'
import useSelectEntities from '@hooks/useSelectEntities'
import useSelector from '@hooks/useSelector'

import type { CampaignItemModel, OrderModel } from '@models/types'
import defaultFormState, { requiredFields } from '@models/reconciliationItem'

import NoticeBox from '@components/NoticeBox'

import { confirmAction } from '@functions/entity'

import ActualTotal from './components/ActualTotal'
import ReconciliationListItem from './components/ReconciliationListItem'

const MODAL_KEY = 'BulkReconcileModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const BulkReconcileModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { callbacks: { deselectAll }, entityType, selectedEntityIds } = modalPayload

  const { campaignItems, orders } = useSelector(reduxState => reduxState.entities)

  const entities = entityType === 'order' ? orders : campaignItems

  const entitiesSelected = useSelectEntities({ initSelectedIds: selectedEntityIds })
  const { callbacks: { toggleId }, selectedEntityIds: updatedSelectedEntityIds, hasSelectedEntities } = entitiesSelected

  const filteredItems = updatedSelectedEntityIds.map(id => entities[id]).filter(i => i)

  const { callbacks: { bulkReconcileOrders }, updating: updatingOrder } = useOrder()
  const { callbacks: { bulkReconcileCampaignItems }, updating: updatingCampaignItem } = useCampaignItem()

  const itemForm = useForm(defaultFormState, {
    requiredFields,
    validateOn: 'change',
  })
  const {
    entityState,
    entityState: {
      invoice_reference,
      invoice_date,
      paid_by,
      invoice_notes,
    },
    errors,
    handlers,
    saveEnabled,
  } = itemForm

  const bulkReconcilePayload = {
    callbacks: {
      action: entityType === 'order' ? bulkReconcileOrders : bulkReconcileCampaignItems,
      afterAction: () => {
        closeModal()
        if (deselectAll) deselectAll()
      },
    },
    options: {
      entityParams: { entity: entityState, entityIds: updatedSelectedEntityIds },
      icon: 'warning',
      confirmButtonColor: '#43ac6a',
    },
    strings: {
      actionString: 'Reconcile',
      entityName: 'Items',
      confirmString: 'Reconciling multiple items together will update the actual total on all the invoices associated with the orders you are trying to reconcile. Are you sure you want to proceed?',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Reconcile" titleSecondLine="Items" />

      <SidebarModal.Content>
        <Form>
          <Form.Row>
            <Form.Field errorMessage={errors.invoice_reference} label="* Invoice #">
              <input name="invoice_reference" type="text" value={invoice_reference} {...handlers} />
            </Form.Field>

            <Form.Field errorMessage={errors.invoice_date} label="* Invoice Date">
              <input name="invoice_date" type="date" value={invoice_date || ''} {...handlers} />
            </Form.Field>
          </Form.Row>

          <Form.Field label="Paid by" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="paid_by" value={paid_by} {...handlers}>
              <option value="">Not Selected...</option>
              <option value="agency">Paid by Agency</option>
              <option value="agent">Paid by Agent</option>
              <option value="commission">Paid from Commission</option>
              <option value="vendor">Paid by Vendor</option>
            </select>
          </Form.Field>

          <Form.Field label="Comments" marginTop="large">
            <textarea
              name="invoice_notes"
              style={{ height: 60, resize: 'vertical' }}
              // @ts-expect-error TS2322
              type="text"
              value={invoice_notes}
              {...handlers}
            />
          </Form.Field>
        </Form>

        <SectionDivider boxProps={{ marginBottom: 'none' }}>SELECTED ORDERS</SectionDivider>

        <NoticeBox variant="whiteGrey">
          <NoticeBox.Title>
            <FontAwesomeIcon icon={faWarning} />
            &nbsp;Your Invoices will be affected
          </NoticeBox.Title>

          <NoticeBox.Paragraph>
            Reconciling multiple items together will update the
            actual total on all of the invoices associated with the following orders.
          </NoticeBox.Paragraph>
        </NoticeBox>

        {filteredItems.map((item: CampaignItemModel | OrderModel, index) => (
          <ReconciliationListItem
            callbacks={{ toggleId }}
            isLastItem={filteredItems.length - 1 === index}
            item={item}
            key={item.id}
          />
        ))}

        {/*
         // @ts-expect-error TS2322 */}
        <ActualTotal items={filteredItems} />
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!hasSelectedEntities || !saveEnabled}
          loading={updatingCampaignItem || updatingOrder}
          // @ts-expect-error TS2345
          onClick={() => confirmAction(bulkReconcilePayload)}
          size="large"
        >
          Reconcile Orders
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <BulkReconcileModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
